import React, { useMemo, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminWidgetTitle,
  AdminSupportingText,
  AdminMetric,
  AdminStatLine,
  AdminHeaderAction,
  AdminLinkAction,
  AdminDetailTableHeader,
  AdminTableBody,
  ADMIN_METRIC_PANEL_CLASS,
  ADMIN_CONTENT_PANEL_CLASS,
  ADMIN_TABLE_CLASS,
  ADMIN_TOOLBAR_CLASS,
} from '../shared/adminUx';
import { findReportById, formatDateDisplay, scopeLabel } from './adminReportsData';
import { getReportData } from '../Moderation/ModerationReports';
import { triggerPdfDownload } from './pdfDownload';
import * as echarts from 'echarts';

const CHART_COLORS = {
  primary: '#2E00FF',
  softpurple: '#7B62D9',
  freshgreen: '#03A62C',
  warmyellow: '#F2C02D',
  alertred: '#FB1912',
  axisLabel: '#6B7280',
  splitLine: '#E5E7EB',
  text: '#232A2E',
};

const EChartComponent = ({ option, style = { width: '100%', height: '280px' } }) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !option) return;
    if (chartInstanceRef.current) chartInstanceRef.current.dispose();
    const chart = echarts.init(chartRef.current);
    chartInstanceRef.current = chart;
    chart.setOption(option);
    const handleResize = () => chart.resize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, [option]);

  return <div ref={chartRef} style={style} />;
};

const formatN = (n) => (n == null ? '-' : Number(n).toLocaleString());

const splitPeriod = (period) => {
  if (!period) return { from: '—', to: '—' };
  const s = String(period);
  const parts = s.split(' - ');
  if (parts.length >= 2) return { from: parts[0], to: parts.slice(1).join(' - ') };
  return { from: s, to: '—' };
};

function SummaryTable({ title, columns, rows }) {
  return (
    <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
      <div className="py-2.5 px-4 border-b border-lightgray">
        <AdminWidgetTitle className="text-offblack text-sm">{title}</AdminWidgetTitle>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-offblack">
          <thead>
            <tr className="border-b border-lightgray bg-pastalBlueWhite/50">
              {columns.map((c) => (
                <th key={c.key} className="text-left py-2 px-4">
                  <AdminDetailTableHeader>{c.label}</AdminDetailTableHeader>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="border-b border-lightgray last:border-0">
                {columns.map((c) => (
                  <td key={c.key} className="py-2 px-4">
                    <AdminTableBody>{row[c.key]}</AdminTableBody>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashboardMainContainer>
  );
}

function ModerationSummaryReport({ report }) {
  const reportData = useMemo(() => getReportData('2026-01-01', '2026-01-31'), []);

  const caseVolumeTrendOption = useMemo(() => {
    const data = reportData?.caseVolumeTrend;
    if (!data?.length) return null;
    return {
      backgroundColor: 'transparent',
      tooltip: { trigger: 'axis', textStyle: { color: CHART_COLORS.text, fontSize: 11 } },
      title: {
        text: 'Moderation Case Volume Trend',
        subtext: `${report.reportPeriod} · weekly`,
        left: 0,
        top: 0,
        textStyle: { fontSize: 13, color: CHART_COLORS.text },
        subtextStyle: { fontSize: 10, color: CHART_COLORS.axisLabel },
      },
      legend: { data: ['Created', 'Closed'], bottom: 0, textStyle: { fontSize: 11, color: CHART_COLORS.text } },
      grid: { top: 56, right: 24, bottom: 36, left: 48 },
      xAxis: {
        type: 'category',
        data: data.map((d) => d.week),
        axisLabel: { color: CHART_COLORS.axisLabel, fontSize: 10 },
        axisLine: { show: false },
        axisTick: { show: false },
        boundaryGap: false,
      },
      yAxis: {
        type: 'value',
        axisLabel: { color: CHART_COLORS.axisLabel, fontSize: 10 },
        splitLine: { lineStyle: { color: CHART_COLORS.splitLine, type: 'dashed' } },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      series: [
        {
          type: 'line',
          name: 'Created',
          data: data.map((d) => d.created),
          smooth: true,
          lineStyle: { color: CHART_COLORS.primary, width: 2 },
          itemStyle: { color: CHART_COLORS.primary },
          symbol: 'circle',
          symbolSize: 6,
        },
        {
          type: 'line',
          name: 'Closed',
          data: data.map((d) => d.closed),
          smooth: true,
          lineStyle: { color: CHART_COLORS.freshgreen, width: 2 },
          itemStyle: { color: CHART_COLORS.freshgreen },
          symbol: 'circle',
          symbolSize: 6,
        },
      ],
    };
  }, [report.reportPeriod, reportData]);

  const outcomeHorizontalBarOption = useMemo(
    () => ({
      backgroundColor: 'transparent',
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      title: {
        text: 'Moderation Outcome Breakdown',
        subtext: report.reportPeriod,
        left: 0,
        top: 0,
        textStyle: { fontSize: 13, color: CHART_COLORS.text },
        subtextStyle: { fontSize: 10, color: CHART_COLORS.axisLabel },
      },
      grid: { top: 56, right: 48, bottom: 24, left: 120 },
      xAxis: {
        type: 'value',
        axisLabel: { color: CHART_COLORS.axisLabel, fontSize: 10 },
        splitLine: { lineStyle: { color: CHART_COLORS.splitLine, type: 'dashed' } },
      },
      yAxis: {
        type: 'category',
        data: ['Other Final Outcome', 'Suspend Account', 'Warning Issued', 'Hide Content', 'No Action'],
        axisLabel: { fontSize: 10, color: CHART_COLORS.text },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      series: [
        {
          type: 'bar',
          data: [68, 104, 216, 428, 312],
          barMaxWidth: 22,
          itemStyle: {
            color: (params) =>
              [CHART_COLORS.axisLabel, CHART_COLORS.softpurple, CHART_COLORS.warmyellow, CHART_COLORS.alertred, CHART_COLORS.primary][
                params.dataIndex
              ],
          },
          label: { show: true, position: 'right', fontSize: 10 },
        },
      ],
    }),
    [report.reportPeriod]
  );

  const surfaceDonutOption = useMemo(
    () => ({
      backgroundColor: 'transparent',
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      title: {
        text: 'Flagged Surface Share',
        subtext: report.reportPeriod,
        left: 'center',
        top: 0,
        textStyle: { fontSize: 13, color: CHART_COLORS.text },
        subtextStyle: { fontSize: 10, color: CHART_COLORS.axisLabel },
      },
      legend: { bottom: 0, textStyle: { fontSize: 10 } },
      series: [
        {
          type: 'pie',
          radius: ['42%', '68%'],
          center: ['50%', '55%'],
          avoidLabelOverlap: true,
          itemStyle: { borderRadius: 4, borderColor: '#fff', borderWidth: 2 },
          label: { formatter: '{b}\n{d}%', fontSize: 10 },
          data: [
            { value: 462, name: 'Posts' },
            { value: 389, name: 'Reviews' },
            { value: 433, name: 'Messages' },
          ],
        },
      ],
    }),
    [report.reportPeriod]
  );

  const flagReasonsBarOption = useMemo(
    () => ({
      backgroundColor: 'transparent',
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      title: {
        text: 'Top Flag Reasons',
        subtext: report.reportPeriod,
        left: 0,
        top: 0,
        textStyle: { fontSize: 13, color: CHART_COLORS.text },
        subtextStyle: { fontSize: 10, color: CHART_COLORS.axisLabel },
      },
      grid: { top: 56, right: 24, bottom: 48, left: 48 },
      xAxis: {
        type: 'category',
        data: ['Spam / Scam', 'Abusive Content', 'Misleading Information', 'Policy Breach', 'Other'],
        axisLabel: { color: CHART_COLORS.axisLabel, fontSize: 10, rotate: 20 },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLabel: { color: CHART_COLORS.axisLabel, fontSize: 10 },
        splitLine: { lineStyle: { color: CHART_COLORS.splitLine, type: 'dashed' } },
      },
      series: [
        {
          type: 'bar',
          data: [298, 241, 198, 174, 113],
          barMaxWidth: 28,
          itemStyle: { color: CHART_COLORS.primary },
          label: { show: true, position: 'top', fontSize: 10 },
        },
      ],
    }),
    [report.reportPeriod]
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {[
          ['Cases Created', '1,284'],
          ['Cases Closed', '1,128'],
          ['Avg Resolution Time', '14.8h'],
          ['SLA Compliance', '91.6%'],
          ['Appeal Cases Created', '14'],
          ['Content Removed Rate', '25.4%'],
        ].map(([label, val]) => (
          <DashboardMainContainer key={label} className={ADMIN_METRIC_PANEL_CLASS}>
            <AdminSupportingText className="text-offblack opacity-80 mb-1">{label}</AdminSupportingText>
            <AdminMetric size="large" className="text-offblack block">{val}</AdminMetric>
          </DashboardMainContainer>
        ))}
      </div>

      <SummaryTable
        title="Case Volume Summary"
        columns={[
          { key: 'metric', label: 'Metric' },
          { key: 'value', label: 'Value' },
        ]}
        rows={[
          { metric: 'New Cases', value: '1,284' },
          { metric: 'Closed Cases', value: '1,128' },
          { metric: 'Open at Period End', value: '156' },
          { metric: 'Linked Follow-up Cases', value: '22' },
          { metric: 'Appeal Cases Created', value: '14' },
        ]}
      />

      <SummaryTable
        title="Outcome Breakdown"
        columns={[
          { key: 'outcome', label: 'Outcome' },
          { key: 'count', label: 'Count' },
          { key: 'rate', label: 'Rate' },
        ]}
        rows={[
          { outcome: 'No Action', count: '312', rate: '27.7%' },
          { outcome: 'Hide Content', count: '428', rate: '37.9%' },
          { outcome: 'Warning Issued', count: '216', rate: '19.1%' },
          { outcome: 'Suspend Account', count: '104', rate: '9.2%' },
          { outcome: 'Other Final Outcome', count: '68', rate: '6.1%' },
        ]}
      />

      <SummaryTable
        title="Surface Breakdown"
        columns={[
          { key: 'surface', label: 'Surface' },
          { key: 'cases', label: 'Cases' },
        ]}
        rows={[
          { surface: 'Posts', cases: '462' },
          { surface: 'Reviews', cases: '389' },
          { surface: 'Messages', cases: '433' },
        ]}
      />

      <SummaryTable
        title="Top Flag Reasons"
        columns={[
          { key: 'reason', label: 'Flag Reason' },
          { key: 'count', label: 'Count' },
        ]}
        rows={[
          { reason: 'Spam / Scam', count: '298' },
          { reason: 'Abusive Content', count: '241' },
          { reason: 'Misleading Information', count: '198' },
          { reason: 'Policy Breach', count: '174' },
          { reason: 'Other', count: '113' },
        ]}
      />

      <SummaryTable
        title="Resolution Performance"
        columns={[
          { key: 'metric', label: 'Metric' },
          { key: 'value', label: 'Value' },
        ]}
        rows={[
          { metric: 'Avg First Check Time', value: '4.2h' },
          { metric: 'Avg Closure Time', value: '14.8h' },
          { metric: 'Within SLA', value: '91.6%' },
          { metric: 'Over SLA', value: '8.4%' },
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DashboardMainContainer className={`${ADMIN_CONTENT_PANEL_CLASS} p-3`}>
          {caseVolumeTrendOption ? <EChartComponent option={caseVolumeTrendOption} /> : null}
        </DashboardMainContainer>
        <DashboardMainContainer className={`${ADMIN_CONTENT_PANEL_CLASS} p-3`}>
          <EChartComponent option={outcomeHorizontalBarOption} />
        </DashboardMainContainer>
        <DashboardMainContainer className={`${ADMIN_CONTENT_PANEL_CLASS} p-3`}>
          <EChartComponent option={surfaceDonutOption} style={{ width: '100%', height: '300px' }} />
        </DashboardMainContainer>
        <DashboardMainContainer className={`${ADMIN_CONTENT_PANEL_CLASS} p-3`}>
          <EChartComponent option={flagReasonsBarOption} />
        </DashboardMainContainer>
      </div>

      <DashboardMainContainer className={`${ADMIN_CONTENT_PANEL_CLASS} p-5`}>
        <AdminWidgetTitle className="text-offblack mb-2 text-sm">Report Notes</AdminWidgetTitle>
        <AdminSupportingText className="text-offblack text-sm">
          Prepared By: {report.generatedBy === 'System' ? 'System' : report.generatedBy}
        </AdminSupportingText>
        {report.notes ? (
          <AdminSupportingText className="text-offblack text-sm mt-2">
            Notes: {report.notes}
          </AdminSupportingText>
        ) : (
          <AdminSupportingText className="text-offblack text-sm mt-2">
            Notes: Monthly moderation summary for internal reference.
          </AdminSupportingText>
        )}
      </DashboardMainContainer>
    </div>
  );
}

function GenericAreaReport({ report }) {
  const metrics = useMemo(() => {
    const area = report.reportArea;
    if (area === 'Platform' || area === 'Platform Overview') {
      return [
        ['Total customers', '48,290'],
        ['Total businesses', '12,104'],
        ['Active posts', '186,400'],
        ['New reviews (period)', '24,180'],
      ];
    }
    if (area === 'Compliance') {
      return [
        ['Applications received', '812'],
        ['Approvals', '624'],
        ['Rejections', '44'],
        ['Expiring items', '118'],
      ];
    }
    if (area === 'Fraud & Security') {
      return [
        ['Fraud cases opened', '96'],
        ['Suspicious activity events', '1,420'],
        ['Account restrictions', '214'],
        ['Bans', '38'],
      ];
    }
    if (area === 'Support Tickets') {
      return [
        ['Tickets created', '3,842'],
        ['Tickets resolved', '3,618'],
        ['SLA met', '94.2%'],
        ['Reopen rate', '4.1%'],
      ];
    }
    if (area === 'Billing & Subscriptions') {
      return [
        ['Active subscriptions', '18,420'],
        ['Failed payments', '612'],
        ['Refunds', '84'],
        ['Disputes', '22'],
      ];
    }
    if (area === 'Customers') {
      return [
        ['Registered customers', '52,180'],
        ['Active in period', '41,920'],
        ['New sign-ups', '3,240'],
        ['Account closures', '186'],
      ];
    }
    if (area === 'Businesses') {
      return [
        ['Active businesses', '12,104'],
        ['New registrations', '842'],
        ['Verification pending', '126'],
        ['Suspended accounts', '38'],
      ];
    }
    if (area === 'Posts') {
      return [
        ['Posts published', '48,920'],
        ['Posts flagged', '1,024'],
        ['Posts removed', '312'],
        ['Featured posts', '2,180'],
      ];
    }
    if (area === 'Reviews') {
      return [
        ['Reviews submitted', '24,180'],
        ['Average rating', '4.6'],
        ['Reviews flagged', '418'],
        ['Reviews removed', '96'],
      ];
    }
    return [
      ['Period total', '—'],
      ['Notes', report.notes || '—'],
    ];
  }, [report.reportArea, report.notes]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics.map(([label, val]) => (
          <DashboardMainContainer key={label} className={ADMIN_METRIC_PANEL_CLASS}>
            <AdminSupportingText className="text-offblack opacity-80 mb-1">{label}</AdminSupportingText>
            <AdminMetric size="large" className="text-offblack block">{val}</AdminMetric>
          </DashboardMainContainer>
        ))}
      </div>
      <SummaryTable
        title="Summary"
        columns={[
          { key: 'label', label: 'Item' },
          { key: 'value', label: 'Value' },
        ]}
        rows={[
          { label: 'Module', value: report.reportArea },
          { label: 'Period from', value: splitPeriod(report.reportPeriod).from },
          { label: 'Period to', value: splitPeriod(report.reportPeriod).to },
          { label: 'Frequency', value: report.frequency },
          {
            label: 'Notes',
            value: report.notes || 'Structured snapshot for the selected period.',
          },
        ]}
      />
      <AdminSupportingText className="text-mediumgray block">
        Charts may be included in PDF output for this area when metrics support comparison or trends. This preview shows
        headline figures and summary tables only.
      </AdminSupportingText>
    </div>
  );
}

export default function AdminReportViewer() {
  const { reportId } = useParams();
  const report = useMemo(() => findReportById(reportId), [reportId]);

  if (!report) {
    return (
      <AdminLayout>
        <div className="tp-container-main">
          <div>
            <AdminPageTitle>Report not found</AdminPageTitle>
            <AdminLinkAction to="/admin/reports">Back to Reports</AdminLinkAction>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const scope = scopeLabel(report.generatedBy, report.frequency);
  const showModerationBody = report.template === 'moderation-summary';

  const handleDownload = () => triggerPdfDownload(report.id);

  return (
    <AdminLayout>
      <div className="tp-container-main">
        <div>
        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 w-full min-w-0">
            <div className="space-y-3 min-w-0">
              <AdminPageTitle className="!mb-0">{report.reportName}</AdminPageTitle>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                <AdminStatLine label="Module:">{report.reportArea}</AdminStatLine>
                <AdminStatLine label="Frequency:">{report.frequency}</AdminStatLine>
                <AdminStatLine label="Period From:">{splitPeriod(report.reportPeriod).from}</AdminStatLine>
                <AdminStatLine label="Period To:">{splitPeriod(report.reportPeriod).to}</AdminStatLine>
                <AdminStatLine label="Generated On:">{formatDateDisplay(report.generatedOn)}</AdminStatLine>
                <AdminStatLine label="Generated By:">{report.generatedBy}</AdminStatLine>
                <AdminStatLine label="Generated By Scope:">{scope}</AdminStatLine>
                <AdminStatLine label="Status:">{report.status || 'Ready'}</AdminStatLine>
              </div>
            </div>
            <AdminHeaderAction className="!h-[36px] shrink-0" onClick={handleDownload}>
              Download PDF
            </AdminHeaderAction>
          </div>
        </div>

        {showModerationBody ? <ModerationSummaryReport report={report} /> : <GenericAreaReport report={report} />}
        </div>
      </div>
    </AdminLayout>
  );
}
